/*
 * Thin client for SAM Cloud's public station API:
 *   - station data  -> stream mount + cover-art base + station name
 *   - now-playing   -> current title / artist / cover (polled)
 *   - history       -> recently played tracks (lazy, on demand)
 * The live stream itself is served by Triton (streamtheworld) and played
 * through a native <audio> element — see RadioPlayer.tsx.
 */

export type PlayerLabels = {
  play: string
  pause: string
  mute: string
  unmute: string
  volume: string
  live: string
  stationName: string
  recentlyPlayed: string
  visitWebsite: string
  close: string
}

// Block attribute: how the stream language is chosen. 'auto' follows the WPML
// page language (resolved server-side in functions.php).
export type StreamLanguage = 'auto' | 'de' | 'en'

export type PlayerConfig = {
  // Station credentials for the SAM Cloud metadata API (now playing / history).
  stationId: string
  token: string
  // Optional direct stream URL. When empty, the stream URL is derived from the
  // SAM Cloud relay (see fetchStation). Set via .env to use a higher-quality
  // stream than the public 64 kbps relay.
  streamUrl?: string
  // Optional ICF Radio website link (ICF network settings); empty = no link rendered.
  websiteUrl?: string
  // Pin a compact player to the bottom of the viewport (radio page); also hides
  // the website link. Set per block instance via the editor toggle.
  floating?: boolean
  labels: PlayerLabels
}

export type Station = {
  name: string | null
  streamUrl: string | null
  coverArtBase: string
  fallbackCover: string
}

export type Track = {
  title: string
  artist: string
  mediaItemId: string
}

export type HistoryEntry = {
  title: string
  artist: string
  cover: string
  playedAt: Date | null
}

const API_BASE = 'https://listen.samcloud.com/webapi/station'
const STREAM_BASE =
  'https://playerservices.streamtheworld.com/api/livestream-redirect'

export const POLL_INTERVAL = 12_000
export const HISTORY_COUNT = 6

type RawRelay = { Value?: { Mount?: string | null } }

type RawStation = {
  StationName?: string
  CoverArtBase?: string
  LogoUrl?: string
  Relays?: RawRelay[]
}

type RawTrack = {
  Title?: string
  Artist?: string
  MediaItemId?: string
}

type RawNowPlaying = { m_Item2?: RawTrack }

type RawHistoryItem = RawTrack & {
  DatePlayed?: string
  Picture?: string | null
}

function withProtocol(url: string): string {
  return url.startsWith('//') ? `https:${url}` : url
}

// SAM serializes dates as "/Date(1781183142000+0000)/".
function parseMsDate(value: string | undefined): Date | null {
  if (!value) return null
  const match = /\/Date\((\d+)/.exec(value)
  return match ? new Date(Number(match[1])) : null
}

export function coverUrlFor(station: Station, mediaItemId: string): string {
  if (station.coverArtBase) {
    return station.coverArtBase.replace(
      'PLACEHOLDER_KEY',
      `${mediaItemId}_144x144.jpg`
    )
  }
  return station.fallbackCover
}

export async function fetchStation(
  stationId: string,
  token: string
): Promise<Station> {
  const res = await fetch(`${API_BASE}/${stationId}?token=${token}&format=json`)
  const data = (await res.json()) as RawStation
  const mount = data.Relays?.[0]?.Value?.Mount
  return {
    name: data.StationName ?? null,
    streamUrl: mount ? `${STREAM_BASE}/${mount}.aac` : null,
    coverArtBase: data.CoverArtBase ?? '',
    fallbackCover: data.LogoUrl ? withProtocol(data.LogoUrl) : ''
  }
}

export async function fetchNowPlaying(
  stationId: string,
  token: string
): Promise<Track | null> {
  const res = await fetch(
    `${API_BASE}/${stationId}/history/npe?token=${token}&format=json`
  )
  const data = (await res.json()) as RawNowPlaying
  const item = data.m_Item2
  if (!item) return null
  return {
    title: item.Title ?? '',
    artist: item.Artist ?? '',
    mediaItemId: item.MediaItemId ?? ''
  }
}

export async function fetchHistory(
  stationId: string,
  token: string,
  station: Station | null
): Promise<HistoryEntry[]> {
  // Two extra entries: the first mirrors the currently playing track (the bar
  // already shows it) and one spare in case an entry has no usable metadata.
  const top = HISTORY_COUNT + 2
  const res = await fetch(
    `${API_BASE}/${stationId}/history?token=${token}&top=${top}&mediaTypeCodes=MUS&format=json`
  )
  const items = (await res.json()) as RawHistoryItem[]
  if (!Array.isArray(items)) return []
  return items.slice(1, 1 + HISTORY_COUNT).map((item) => ({
    title: item.Title ?? '',
    artist: item.Artist ?? '',
    cover:
      item.Picture ||
      (item.MediaItemId && station
        ? coverUrlFor(station, item.MediaItemId)
        : ''),
    playedAt: parseMsDate(item.DatePlayed)
  }))
}
